import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import logError from '@alltrails/core/utils/logError';
import { Costings, CustomRouteActivityType } from '@alltrails/types/CustomRoute';
import { useAppDispatch, useAppSelector } from '../../redux';
import { setCostingsConfig, setToastAlerts, setToastMessage } from '../../redux/customRoute';
import { useGetInfoQuery, useLazyGetRouteQuery } from '../../redux/customRoutesApi';
import type { GetRoutePayload, RoutingMode, ServerRoute } from '../../redux/customRoutesApi';
import { getGeneralError } from '../../utils/customRoutes/constants';
import { routeAlongLineString } from '../../utils/customRoutes/routeAlongLineString';

type RoutingOptions = {
  newActivity?: CustomRouteActivityType;
  routes: ServerRoute[];
  costings?: Costings;
  onFetch?: () => void;
};

export const activityToRoutingMode: Record<CustomRouteActivityType, RoutingMode> = {
  hiking: 'hike',
  backpacking: 'hike',
  'trail-running': 'hike',
  running: 'hike',
  'road-biking': 'bike',
  'mountain-biking': 'mtb',
  'off-road-driving': 'offroad',
  'scenic-driving': 'car'
};

export const useGetRoutePayload = () => {
  // The enum-based costings are selected individually so that routing is not invalidated every time the number-based
  // costings are reset, which happens on every route edit
  const { activity, elevationGain, optimizeFor, surfaceType } = useAppSelector(state => ({
    activity: state.customRoute.route.activityType,
    elevationGain: state.customRoute.costings?.elevationGain,
    optimizeFor: state.customRoute.costings?.optimizeFor,
    surfaceType: state.customRoute.costings?.surfaceType
  }));

  const { data: infoData } = useGetInfoQuery();
  const routingMode =
    (infoData?.activityTypes.find(type => type.uid === activity)?.routingMode as RoutingMode) || activityToRoutingMode[activity] || 'hike';

  return useCallback(
    ({ routes, costings: costingsOverride, newActivity }: Omit<RoutingOptions, 'onFetch'>): GetRoutePayload => {
      const activeRoutingMode = newActivity ? activityToRoutingMode[newActivity] : routingMode;

      // Only the enum-based costings are maintained as the user makes edits to the route. Omit the key entirely when
      // there is nothing to send so that a plain route edit does not post an empty costings object.
      const activeCostings: Partial<Costings> = {
        ...(optimizeFor && { optimizeFor }),
        ...(elevationGain && { elevationGain }),
        ...(surfaceType?.length && { surfaceType }),
        ...costingsOverride
      };

      return {
        routingMode: activeRoutingMode,
        routes,
        activityTypeChange: activeRoutingMode !== routingMode,
        ...(Object.keys(activeCostings).length ? { costings: activeCostings } : {})
      };
    },
    [routingMode, elevationGain, optimizeFor, surfaceType]
  );
};

const useRouting = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { costingsConfig, isRoutingAlongSuggestedRoute, suggestedRoute } = useAppSelector(state => ({
    costingsConfig: state.customRoute.costingsConfig,
    isRoutingAlongSuggestedRoute: state.customRoute.isRoutingAlongSuggestedRoute,
    suggestedRoute: state.customRoute.suggestedRoute
  }));

  // Ref to store the isFetching state to minimize dependencies in the routing function
  const isFetchingRef = useRef(false);

  const { isSuccess: isInfoSuccess, data: infoData } = useGetInfoQuery();
  const [fetch] = useLazyGetRouteQuery();
  const getRoutePayload = useGetRoutePayload();

  const serverRouting = useCallback(
    async ({ routes, costings: costingsOverride, newActivity, onFetch }: RoutingOptions) => {
      if (isFetchingRef.current) {
        return { isSuccess: false };
      }

      isFetchingRef.current = true;
      onFetch?.();

      const {
        isSuccess: isQuerySuccess,
        isError,
        data: queryData,
        error
      } = await fetch(
        getRoutePayload({ routes, costings: costingsOverride, newActivity }),
        true // preferCacheValue
      );
      let isSuccess = isQuerySuccess;
      let data = queryData;

      if (isError) {
        dispatch(setToastMessage(getGeneralError(intl)));
        logError(new Error('Failed to fetch routing data', { cause: error }));
      }

      if (isQuerySuccess && queryData?.error) {
        isSuccess = false;
        data = undefined;
        const { iconCode, message } = queryData.error;
        dispatch(setToastMessage({ iconCode, message }));
      }

      if (isQuerySuccess && queryData?.alerts?.length) {
        dispatch(setToastAlerts([...queryData.alerts]));
      }

      isFetchingRef.current = false;
      return { isSuccess, data };
    },
    [dispatch, fetch, getRoutePayload, intl]
  );

  useEffect(() => {
    // Since the info query has no params we only need to set the costingsConfig once across all instances of useRouting
    if (!costingsConfig && isInfoSuccess && infoData) {
      dispatch(setCostingsConfig(infoData.costings));
    }
  }, [costingsConfig, isInfoSuccess, infoData, dispatch]);

  const suggestedRouteRouting = useCallback(
    async ({ routes, onFetch }: RoutingOptions) => {
      onFetch?.();
      const result = !suggestedRoute ? { isSuccess: false, data: undefined } : routeAlongLineString(routes, suggestedRoute);

      if (!result.isSuccess) {
        dispatch(setToastMessage({ message: 'Failed to route along suggested route.', iconCode: 'error' }));
      }

      return result;
    },
    [suggestedRoute, dispatch]
  );

  const routing = isRoutingAlongSuggestedRoute ? suggestedRouteRouting : serverRouting;
  return routing;
};

export default useRouting;
